// Public release manifest fetcher.
//
// The bucket at storage.googleapis.com/katenaria-downloads/coprocessor/ignition
// is public-read and CORS-enabled (Access-Control-Allow-Origin: *), so the
// landing page fetches latest.json directly from the browser at page mount.
//
// We deliberately avoid build-time stamping. Module releases just write to
// the bucket; the next page load picks them up. This keeps landing deploys
// independent from module-release CI.

export type Platform =
  | "linux-amd64"
  | "linux-arm64"
  | "darwin-arm64"
  | "windows-amd64";

export interface ReleaseArtifact {
  url: string;
  sha256: string;
}

export interface ReleaseManifest {
  version: string;
  released_at: string; // ISO timestamp
  artifacts: Record<Platform, ReleaseArtifact>;
}

const DEFAULT_LATEST_RELEASE_URL =
  "https://storage.googleapis.com/katenaria-downloads/coprocessor/ignition/latest.json";

// Dev/QA hatch: ?latestUrl=/test-fixtures/empty.json on the page query
// string redirects the manifest fetch at runtime. This is how we exercise
// fallback states without mutating the bucket's real latest.json. The
// override is read once per fetch call (not memoized) so navigation that
// changes the search string (HMR, Storybook) picks it up.
function resolveLatestUrl(): string {
  if (typeof window === "undefined") return DEFAULT_LATEST_RELEASE_URL;
  try {
    const override = new URLSearchParams(window.location.search).get(
      "latestUrl",
    );
    if (override) return override;
  } catch {
    // location may be inaccessible in strange embed contexts; fall through.
  }
  return DEFAULT_LATEST_RELEASE_URL;
}

// Exported as a getter for callers that want to surface the URL (e.g.
// dev-mode logging). Production code should not depend on this — the
// fetcher resolves the URL itself.
export const LATEST_RELEASE_URL = DEFAULT_LATEST_RELEASE_URL;

// Returns the manifest, or null when the bucket has no stable release yet
// (404 / placeholder shape with null version). Throws on transient network
// errors so callers can decide whether to surface a retry affordance.
export async function fetchLatestRelease(
  signal?: AbortSignal,
): Promise<ReleaseManifest | null> {
  const url = resolveLatestUrl();
  // cache: 'no-store' is intentional — latest.json has 60s edge cache, but
  // we want hard reloads to always pick up new releases. Without this, the
  // browser cache would otherwise pin the response for the whole session.
  const res = await fetch(url, { signal, cache: "no-store" });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(
      `latest.json fetch failed: HTTP ${res.status} (${url})`,
    );
  }
  const raw = (await res.json()) as unknown;
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Partial<ReleaseManifest>;
  // Placeholder shape: { version: null, released_at: null, artifacts: {} }
  if (!obj.version || !obj.artifacts) return null;
  return obj as ReleaseManifest;
}

// Display version: drop the 4th build component. The manifest carries
// e.g. "0.9.2.1745825142"; humans only care about "0.9.2". The full
// string stays available for tooltips / debugging.
export function displayVersion(fullVersion: string): string {
  const parts = fullVersion.split(".");
  if (parts.length >= 3) return parts.slice(0, 3).join(".");
  return fullVersion;
}

// Filename derived from the artifact URL (last path segment). Falls back
// to a synthetic name if the URL is malformed.
export function artifactFilename(
  manifest: ReleaseManifest,
  platform: Platform,
): string {
  const url = manifest.artifacts[platform]?.url;
  if (url) {
    const last = url.split("/").pop();
    if (last) return last;
  }
  return `coprocessor-${manifest.version}-${platform}.modl`;
}

const BUCKET_PREFIX =
  "https://storage.googleapis.com/katenaria-downloads/coprocessor/ignition";

// URL for a platform that may not be in the manifest. Returns the
// manifest's URL when present; otherwise synthesizes from the version
// using the well-known bucket layout. The synthetic URL will 404 if the
// CI hasn't built that platform yet — that's intentional: it surfaces
// the gap honestly rather than silently no-op'ing the click.
export function artifactUrl(
  manifest: ReleaseManifest,
  platform: Platform,
): string {
  const present = manifest.artifacts[platform]?.url;
  if (present) return present;
  return `${BUCKET_PREFIX}/v${manifest.version}/coprocessor-${manifest.version}-${platform}.modl`;
}
